import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { calculateXpReward, awardXp } from '../../services/rewardService';
import { puterCoachingService } from '../../services/puterCoachingService';
import NavigationBar from '../../components/ui/NavigationBar';
import LoadingState from '../../components/ui/LoadingState';
import TechniqueCard from '../../components/TechniqueCard';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import lumoTechniquesService from '../../services/lumoTechniquesService';

const TechniquePracticePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [technique, setTechnique] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Practice flow: intro → scenario → response → feedback → complete
  const [practicePhase, setPracticePhase] = useState('intro');
  const [scenario, setScenario] = useState(null);
  const [scenarioLoading, setScenarioLoading] = useState(false);
  const [userResponse, setUserResponse] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  
  // AI-generated scores (user can adjust)
  const [clarity, setClarity] = useState(70);
  const [confidence, setConfidence] = useState(70);
  const [control, setControl] = useState(70);
  const [close, setClose] = useState(70);
  const executionScore = Math.round((clarity + confidence + control + close) / 4);
  
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [mastery, setMastery] = useState(null);
  const [xpAwarded, setXpAwarded] = useState(null);

  // Load technique and mastery
  useEffect(() => {
    const loadTechnique = async () => {
      try {
        setIsLoading(true);
        setError('');
        const data = await lumoTechniquesService.getTechniqueBySlug(slug);
        if (!data) {
          setError('Technique not found');
          return;
        }
        setTechnique(data);
        try {
          const all = await lumoTechniquesService.getUserTechniqueMastery(user?.id);
          const current = all?.find(m => m.techniqueName === data?.name) || null;
          setMastery(current);
        } catch (mErr) {
          console.warn('Mastery fetch failed', mErr);
        }
      } catch (err) {
        console.error('Error loading technique:', err);
        setError('Failed to load technique');
      } finally {
        setIsLoading(false);
      }
    };

    if (!user) {
      navigate('/authentication');
      return;
    }
    loadTechnique();
    window.scrollTo(0, 0);
  }, [slug, user, navigate]);

  // Generate roleplay scenario
  const generateScenario = async () => {
    if (!technique) return;
    try {
      setScenarioLoading(true);
      const prompt = `You are a sales objection scenario generator. Create a realistic sales scenario where the salesperson needs to use the "${technique.name}" technique. 

Technique: ${technique.tagline}

Generate ONE scenario with:
1. Buyer's objection (1-2 sentences)
2. Context (who, product, situation)
3. Your role (what buyer will say)

Format as: OBJECTION: [text] | CONTEXT: [text] | BUYER_ROLE: [text]`;

      const response = await puterCoachingService.generateCoachingResponse(prompt);
      setScenario(response);
      setPracticePhase('scenario');
    } catch (err) {
      console.error('Error generating scenario:', err);
      setError('Could not generate practice scenario. Try again.');
      setScenarioLoading(false);
    }
  };

  // Get AI feedback on user's response
  const getRoleplayFeedback = async () => {
    if (!technique || !userResponse.trim()) {
      alert('Please provide a response first');
      return;
    }

    try {
      setFeedbackLoading(true);
      const prompt = `You are an expert sales coach evaluating a ${technique.name} technique application.

TECHNIQUE: ${technique.name}
DEFINITION: ${technique.tagline}
STEPS: 
- Step 1: ${technique.step1}
- Step 2: ${technique.step2}
- Step 3: ${technique.step3}

SCENARIO: ${scenario}

SALESPERSON'S RESPONSE: "${userResponse}"

Evaluate on this rubric (0-100 each):
1. Clarity: Is the response clear and easy to understand?
2. Confidence: Does tone convey confidence without pressure?
3. Control: Does it keep conversation on track and handle objection directly?
4. Close: Does it move toward next step or agreement?

Also provide:
- What they did well (1-2 sentences)
- What to improve (1-2 sentences)
- A better example response (30-45 seconds)

RESPOND ONLY IN THIS JSON FORMAT:
{
  "clarity": 0-100,
  "confidence": 0-100,
  "control": 0-100,
  "close": 0-100,
  "strengths": "...",
  "improvements": "...",
  "sample_response": "..."
}`;

      const feedbackText = await puterCoachingService.generateCoachingResponse(prompt);
      
      try {
        const parsed = JSON.parse(feedbackText);
        setFeedback(parsed);
        setClarity(parsed.clarity || 70);
        setConfidence(parsed.confidence || 70);
        setControl(parsed.control || 70);
        setClose(parsed.close || 70);
      } catch (parseErr) {
        console.warn('JSON parse failed, using fallback');
        setFeedback({
          clarity: 75,
          confidence: 75,
          control: 75,
          close: 75,
          strengths: 'Good attempt at applying the technique',
          improvements: 'Review the technique steps and practice again',
          sample_response: feedbackText
        });
        setClarity(75);
        setConfidence(75);
        setControl(75);
        setClose(75);
      }
      
      setPracticePhase('feedback');
    } catch (err) {
      console.error('Error getting feedback:', err);
      setError('Could not generate feedback. Try again.');
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Save the practice rep
  const savePracticeRep = async () => {
    if (!user || !technique) return;
    try {
      setSaving(true);
      await lumoTechniquesService.trackTechniqueApplication({
        userId: user.id,
        techniqueId: technique.id,
        sessionType: 'technique_practice',
        objectionFaced: scenario || '-',
        appliedCorrectly: executionScore >= 70,
        executionScore: Math.max(0, Math.min(100, executionScore)) / 100,
        outcome: executionScore >= 70 ? 'succeeded' : 'needs_work',
        whatWorked: feedback?.strengths || notes || 'Practiced technique',
        whatNeedsWork: feedback?.improvements || ''
      });

      // Award XP
      try {
        const userSkill = (profile?.skill_level === 'advanced') ? 'advanced' : (profile?.skill_level === 'intermediate') ? 'intermediate' : 'beginner';
        const xpRes = calculateXpReward(
          executionScore,
          'roleplay',
          userSkill,
          {
            contentDifficulty: technique?.difficultyLevel ? (technique?.difficultyLevel === 'advanced' ? 3 : technique?.difficultyLevel === 'intermediate' ? 2 : 1) : 1,
            userSkillLevel: userSkill === 'advanced' ? 3 : userSkill === 'intermediate' ? 2 : 1,
            currentLevel: profile?.level || 1
          }
        );
        setXpAwarded(xpRes?.xpAwarded || 0);
        if (profile?.id) {
          await awardXp(profile.id, xpRes?.xpAwarded || 0);
        }
      } catch (xpErr) {
        console.warn('XP award failed', xpErr);
      }

      // Refresh mastery
      try {
        const all = await lumoTechniquesService.getUserTechniqueMastery(user?.id);
        const current = all?.find(m => m.techniqueName === technique?.name) || null;
        setMastery(current);
      } catch (mErr) {
        console.warn('Mastery refresh failed', mErr);
      }

      setPracticePhase('complete');
    } catch (err) {
      console.error('Error saving practice:', err);
      setError('Could not save practice. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetPractice = () => {
    setPracticePhase('intro');
    setScenario(null);
    setUserResponse('');
    setFeedback(null);
    setClarity(70);
    setConfidence(70);
    setControl(70);
    setClose(70);
    setNotes('');
    setXpAwarded(null);
  };

  if (isLoading) {
    return <LoadingState message="Loading technique..." />;
  }

  if (error && practicePhase === 'intro') {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationBar />
        <div className="max-w-4xl mx-auto p-6 mt-6">
          <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg flex items-start gap-3">
            <Icon name="AlertCircle" className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="font-semibold text-red-900">Error</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <NavigationBar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary-100 rounded-xl">
              <Icon name="Target" className="w-8 h-8 text-primary-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Practice: {technique?.name}</h1>
              <p className="text-gray-600">Interactive roleplay practice with AI feedback</p>
            </div>
          </div>
        </div>

        {/* Technique Overview */}
        {technique && <TechniqueCard technique={technique} showFullDetails={true} showIndustryContext={false} />}

        {/* Mastery Status */}
        {mastery && (
          <div className="mb-6 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg border border-primary-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Your Mastery</p>
                <p className="text-lg font-bold text-primary-700 capitalize">{mastery?.masteryLevel}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-600">Attempts</p>
                <p className="text-lg font-bold text-gray-900">{mastery?.timesAttempted || 0}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-600">Success Rate</p>
                <p className="text-lg font-bold text-green-600">{mastery?.successRate ? Math.round(mastery.successRate * 100) : 0}%</p>
              </div>
            </div>
          </div>
        )}

        {/* INTRO PHASE */}
        {practicePhase === 'intro' && (
          <div className="bg-white p-8 rounded-xl border-2 border-gray-200">
            <div className="flex items-start gap-4 mb-6">
              <Icon name="Zap" className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">How This Works</h3>
                <ol className="text-sm text-gray-700 space-y-2 mt-2">
                  <li><strong>1. Generate Scenario:</strong> Get a realistic objection/situation to handle</li>
                  <li><strong>2. Roleplay:</strong> Respond using the {technique?.name} technique</li>
                  <li><strong>3. Get Feedback:</strong> AI coach scores your response using a rubric</li>
                  <li><strong>4. Log Results:</strong> Track your execution and mastery progress</li>
                </ol>
              </div>
            </div>
            <Button 
              onClick={generateScenario} 
              disabled={scenarioLoading}
              className="py-3 px-6 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg"
            >
              {scenarioLoading ? 'Generating...' : 'Start Practice Session'}
            </Button>
          </div>
        )}

        {/* SCENARIO PHASE */}
        {practicePhase === 'scenario' && scenario && (
          <div className="space-y-6">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
              <div className="flex items-start gap-3 mb-4">
                <Icon name="MessageSquare" className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <h3 className="font-bold text-blue-900">Your Scenario</h3>
              </div>
              <p className="text-gray-800 whitespace-pre-wrap text-sm leading-relaxed">{scenario}</p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              <label className="block font-bold text-gray-900 mb-3">Your Response</label>
              <p className="text-sm text-gray-600 mb-3">Use the {technique?.name} technique to respond to this scenario. Speak naturally—imagine you're on a real sales call.</p>
              <textarea
                value={userResponse}
                onChange={(e) => setUserResponse(e.target.value)}
                rows={6}
                placeholder={`Respond here using: ${technique?.step1} → ${technique?.step2} → ${technique?.step3}`}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm font-mono resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-2">{userResponse.length} characters</p>
              
              <div className="flex gap-3 mt-4">
                <Button
                  onClick={getRoleplayFeedback}
                  disabled={feedbackLoading || !userResponse.trim()}
                  className="py-3 px-6 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg disabled:opacity-50"
                >
                  {feedbackLoading ? 'Getting Feedback...' : 'Get AI Feedback'}
                </Button>
                <Button
                  onClick={resetPractice}
                  variant="outline"
                  className="py-3 px-6"
                >
                  Start Over
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* FEEDBACK PHASE */}
        {practicePhase === 'feedback' && feedback && (
          <div className="space-y-6">
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-2">Your Response</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{userResponse}</p>
            </div>

            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
              <div className="flex items-start gap-3 mb-4">
                <Icon name="CheckCircle" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <h3 className="font-bold text-green-900">AI Coach Feedback</h3>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Clarity', value: clarity, setter: setClarity },
                  { label: 'Confidence', value: confidence, setter: setConfidence },
                  { label: 'Control', value: control, setter: setControl },
                  { label: 'Close', value: close, setter: setClose }
                ].map(dim => (
                  <div key={dim.label}>
                    <p className="text-xs font-semibold text-gray-700 mb-2">{dim.label}</p>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{dim.value}</p>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={dim.value}
                        onChange={(e) => dim.setter(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mb-4 p-3 bg-white rounded border border-green-300">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Overall Execution:</span> <span className="text-2xl font-bold text-green-600">{executionScore}%</span>
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">What You Did Well</p>
                  <p className="text-sm text-gray-700 mt-1">{feedback?.strengths}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">What to Improve</p>
                  <p className="text-sm text-gray-700 mt-1">{feedback?.improvements}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Better Example Response</p>
                  <p className="text-sm text-gray-700 mt-1 italic">{feedback?.sample_response}</p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              <label className="block font-bold text-gray-900 mb-2">Notes (Optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="What will you focus on next rep?"
                className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={savePracticeRep}
                disabled={saving}
                className="py-3 px-6 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save & Log Rep'}
              </Button>
              <Button
                onClick={resetPractice}
                variant="outline"
                className="py-3 px-6"
              >
                Try Again
              </Button>
            </div>
          </div>
        )}

        {/* COMPLETE PHASE */}
        {practicePhase === 'complete' && (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-8 text-center">
            <div className="flex justify-center mb-4">
              <Icon name="Award" className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-900 mb-2">Practice Logged!</h2>
            <p className="text-green-800 mb-4">Great work practicing {technique?.name}. Keep reps coming to build mastery.</p>
            
            {xpAwarded && xpAwarded > 0 && (
              <div className="mb-6 p-4 bg-white rounded-lg border border-green-300">
                <p className="text-sm text-gray-700">You earned</p>
                <p className="text-3xl font-bold text-amber-600">+{xpAwarded} XP</p>
              </div>
            )}

            {mastery && (
              <div className="mb-6 p-4 bg-white rounded-lg border border-green-300">
                <p className="text-sm text-gray-700">Your Mastery Level is Now</p>
                <p className="text-lg font-bold text-primary-700 capitalize">{mastery?.masteryLevel}</p>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <Button
                onClick={resetPractice}
                className="py-3 px-6 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg"
              >
                Practice Again
              </Button>
              <Button
                onClick={() => navigate('/app/techniques-library')}
                variant="outline"
                className="py-3 px-6"
              >
                Back to Library
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TechniquePracticePage;
